import { GetPodcastMetricsByEpisodeResponse } from 'api';
import { getPodcastMetricsByEpisode } from 'api/services/dashboard';
import { useMemo } from 'react';
import { useInfiniteQuery } from 'react-query';
import { UseInfiniteQueryOptions } from 'types';
import { usePodcast } from './PodcastContext';
import { metricsQueries } from './queryKeys';
import { episodeListenDataSelector } from './selectors';

type QueryKey = ReturnType<typeof metricsQueries.byEpisode>;

type Options<TData = GetPodcastMetricsByEpisodeResponse> =
  UseInfiniteQueryOptions<
    GetPodcastMetricsByEpisodeResponse,
    QueryKey,
    unknown,
    TData
  >;

function usePodcastMetricsByEpisode<TData = GetPodcastMetricsByEpisodeResponse>(
  opts?: Options<TData>,
) {
  const { podcastId: id } = usePodcast();
  return useInfiniteQuery(
    metricsQueries.byEpisode(id as string),
    ({ pageParam, queryKey: [{ podcastId }] }) =>
      getPodcastMetricsByEpisode(
        { podcastId },
        { nextEpisodePubAtMillis: pageParam },
      ),
    {
      getNextPageParam: (lastPage) =>
        lastPage?.nextEpisodePubAtMillis ?? undefined,
      enabled: !!id,
      ...opts,
    },
  );
}

export function useEpisodeListenData() {
  const { data, ...rest } = usePodcastMetricsByEpisode();

  const episodeStats = useMemo(() => episodeListenDataSelector(data), [data]);
  return { data: episodeStats, ...rest };
}
